/* eslint-disable no-console */
/**
 * Parses Word table HTML and converts it to Volto table block format
 */

import { v4 as uuid } from 'uuid';
import { WordDetector } from './WordDetector';

export class WordTableParser {
  /**
   * Checks if the HTML content contains a Word table
   * @param {string} html - HTML content to check
   * @returns {boolean}
   */
  static hasWordTable(html) {
    const hasTable = /<table/i.test(html);
    const isWordContent = WordDetector.isWordContent(html);

    return hasTable && isWordContent;
  }

  /**
   * Parses Word table HTML and converts to table block format
   * @param {string} html - Word table HTML
   * @param {Object} originalTableData - Original table data to preserve settings
   * @returns {Object|null} Table block data or null if no table found
   */
  parseWordTable(html, originalTableData = null) {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    const table = tempDiv.querySelector('table');

    // Clean Word-specific attributes from the table
    this.cleanWordTableAttributes(table);

    // Parse table structure
    const rows = this.parseTableRows(table);

    if (rows.length === 0) {
      console.log('❌ No rows found in table');
      return null;
    }

    const tableData = {
      // Preserve original table settings or use defaults
      hideHeaders:
        originalTableData?.hideHeaders !== undefined
          ? originalTableData.hideHeaders
          : false,
      fixed:
        originalTableData?.fixed !== undefined ? originalTableData.fixed : true,
      compact:
        originalTableData?.compact !== undefined
          ? originalTableData.compact
          : false,
      basic:
        originalTableData?.basic !== undefined ? originalTableData.basic : true, // Default to true for "very basic" styling
      celled:
        originalTableData?.celled !== undefined
          ? originalTableData.celled
          : true,
      inverted:
        originalTableData?.inverted !== undefined
          ? originalTableData.inverted
          : false,
      striped:
        originalTableData?.striped !== undefined
          ? originalTableData.striped
          : false,
      // Replace rows with parsed Word table data
      rows: rows,
      // Preserve any other original properties
      ...(originalTableData &&
        Object.keys(originalTableData).reduce((acc, key) => {
          if (
            ![
              'hideHeaders',
              'fixed',
              'compact',
              'basic',
              'celled',
              'inverted',
              'striped',
              'rows',
            ].includes(key)
          ) {
            acc[key] = originalTableData[key];
          }
          return acc;
        }, {})),
    };

    return tableData;
  }

  /**
   * Cleans Word-specific attributes from table elements
   * @param {Element} table - Table element to clean
   */
  cleanWordTableAttributes(table) {
    // Remove Word-specific attributes
    const elementsToClean = table.querySelectorAll('*');

    elementsToClean.forEach((el) => {
      // Remove Word-specific attributes
      const attributesToRemove = [];
      for (let i = 0; i < el.attributes.length; i++) {
        const attr = el.attributes[i];
        if (
          attr.name.startsWith('mso-') ||
          attr.name.includes('office') ||
          attr.name === 'border' ||
          attr.name === 'cellpadding' ||
          attr.name === 'cellspacing'
        ) {
          attributesToRemove.push(attr.name);
        }
      }
      attributesToRemove.forEach((attrName) => el.removeAttribute(attrName));

      // Clean Word-specific styles
      if (el.style) {
        const stylesToRemove = [];
        for (let i = 0; i < el.style.length; i++) {
          const styleName = el.style[i];
          if (styleName.startsWith('mso-') || styleName.includes('office')) {
            stylesToRemove.push(styleName);
          }
        }
        stylesToRemove.forEach((styleName) =>
          el.style.removeProperty(styleName),
        );
      }
    });
  }

  /**
   * Parses table rows and converts to table block format
   * @param {Element} table - Table element
   * @returns {Array} Array of row objects
   */
  parseTableRows(table) {
    const rows = [];

    // Handle different table structures (with or without thead/tbody)
    const tableRows = table.querySelectorAll('tr');

    tableRows.forEach((tr, rowIndex) => {
      const cells = this.parseTableCells(tr, rowIndex === 0);
      if (cells.length > 0) {
        rows.push({
          key: uuid(),
          cells: cells,
        });
      }
    });

    return rows;
  }

  /**
   * Parses table cells in a row
   * @param {Element} tr - Table row element
   * @param {boolean} isFirstRow - Whether this is the first row (potential header)
   * @returns {Array} Array of cell objects
   */
  parseTableCells(tr, isFirstRow = false) {
    const cells = [];
    const cellElements = tr.querySelectorAll('td, th');

    cellElements.forEach((cellEl, cellIndex) => {
      // Determine cell type based on element type or position
      const isHeader = cellEl.tagName.toLowerCase() === 'th' || isFirstRow;

      // Convert cell content to Slate format
      const slateContent = this.convertCellContentToSlate(cellEl);

      cells.push({
        key: uuid(),
        type: isHeader ? 'header' : 'data',
        value: slateContent,
      });
    });

    return cells;
  }

  /**
   * Converts cell HTML content to Slate format
   * @param {Element} cellEl - Cell element
   * @returns {Array} Slate content array
   */
  convertCellContentToSlate(cellEl) {
    // Get text content and preserve basic formatting
    const textContent = cellEl.textContent || '';

    // For now, create simple paragraph structure
    // This could be enhanced to preserve more formatting
    if (textContent.trim()) {
      return [
        {
          type: 'p',
          children: [{ text: textContent.trim() }],
        },
      ];
    } else {
      return [
        {
          type: 'p',
          children: [{ text: '' }],
        },
      ];
    }
  }

  /**
   * Creates an empty paragraph in Slate format
   * @returns {Array} Empty Slate paragraph
   */
  static getEmptyParagraph() {
    return [{ type: 'p', children: [{ text: '' }] }];
  }
}
